import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface User {
  id: string;
  username: string;
  display_name: string;
  is_admin?: boolean;
}

interface Passkey {
  id: string;
  device_name: string | null;
  device_type: string | null;
  created_at: string;
  last_used: string | null;
}

interface AuthState {
  // Auth state
  isAuthenticated: boolean;
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  authType: 'passkey' | 'api_key' | 'dev_mode' | 'password' | null;

  // Setup state
  needsSetup: boolean;
  setupChecked: boolean;

  // UI state
  isLoading: boolean;
  error: string | null;
  showLogin: boolean;
  showRegister: boolean;
  showPasswordLogin: boolean;
  passkeys: Passkey[];
  recoveryCodesRemaining: number;

  // Actions
  setAuth: (auth: {
    user: User;
    accessToken: string;
    refreshToken: string;
    authType: 'passkey' | 'api_key' | 'password';
  }) => void;
  setDevMode: () => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setShowLogin: (show: boolean) => void;
  setShowRegister: (show: boolean) => void;
  setShowPasswordLogin: (show: boolean) => void;
  setPasskeys: (passkeys: Passkey[]) => void;
  setRecoveryCodesRemaining: (count: number) => void;
  setNeedsSetup: (needsSetup: boolean) => void;
  setSetupChecked: (checked: boolean) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setUser: (user: User) => void;
  setAuthenticated: (authenticated: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      // Initial state
      isAuthenticated: false,
      user: null,
      accessToken: null,
      refreshToken: null,
      authType: null,
      needsSetup: false,
      setupChecked: false,
      isLoading: false,
      error: null,
      showLogin: false,
      showRegister: false,
      showPasswordLogin: false,
      passkeys: [],
      recoveryCodesRemaining: 0,

      // Actions
      setAuth: ({ user, accessToken, refreshToken, authType }) =>
        set({
          isAuthenticated: true,
          user,
          accessToken,
          refreshToken,
          authType,
          showLogin: false,
          showRegister: false,
          showPasswordLogin: false,
          error: null,
        }),

      setDevMode: () =>
        set({
          isAuthenticated: false,
          user: null,
          accessToken: null,
          refreshToken: null,
          authType: 'dev_mode',
          showLogin: false,
          showRegister: false,
          showPasswordLogin: false,
        }),

      logout: () =>
        set({
          isAuthenticated: false,
          user: null,
          accessToken: null,
          refreshToken: null,
          authType: null,
          passkeys: [],
          recoveryCodesRemaining: 0,
        }),

      setLoading: (isLoading) => set({ isLoading }),
      setError: (error) => set({ error }),
      setShowLogin: (showLogin) => set({ showLogin, showRegister: false, showPasswordLogin: false }),
      setShowRegister: (showRegister) => set({ showRegister, showLogin: false, showPasswordLogin: false }),
      setShowPasswordLogin: (showPasswordLogin) => set({ showPasswordLogin, showLogin: false, showRegister: false }),
      setPasskeys: (passkeys) => set({ passkeys }),
      setRecoveryCodesRemaining: (recoveryCodesRemaining) => set({ recoveryCodesRemaining }),
      setNeedsSetup: (needsSetup) => set({ needsSetup }),
      setSetupChecked: (setupChecked) => set({ setupChecked }),
      setTokens: (accessToken, refreshToken) => set({ accessToken, refreshToken }),
      setUser: (user) => set({ user }),
      setAuthenticated: (isAuthenticated) => set({ isAuthenticated }),
    }),
    {
      name: 'tradedesk-auth',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        authType: state.authType,
      }),
    }
  )
);
